import {
  missionDurations,
  parseEngineHours,
  type FieldMissionHeader,
  type FieldMissionItem,
} from '@groupe-reca/contracts'
import { ArrowLeft, CircleCheck, Clock3, Flag, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useOnline } from '@/shared/hooks/useOnline'
import { runCommand } from '../../application/commands'
import { useOperator } from '../../application/OperatorServices'
import {
  engineHoursErrors,
  formatDuration,
  formatEngineHours,
  itemAddress,
  serverMessage,
} from '../../domain/fieldText'

/**
 * Bilan de fin de sortie, plein écran, avant confirmation (maquettes) : terminées, restantes, problèmes, durées,
 * relevé du compteur au retour (≥ départ), état de l'envoi. Les résidences non résolues restent au bureau : elles
 * ne sont ni effacées ni comptées comme faites. Sans aucun travail, « Annuler le démarrage » (démarrage accidentel)
 * est proposé; le serveur vérifie seul qu'aucun travail n'existe.
 */
export function EndMissionDialog({
  mission,
  items,
  onClose,
  onEnded,
}: {
  mission: FieldMissionHeader
  items: FieldMissionItem[]
  onClose: () => void
  onEnded: () => Promise<void>
}) {
  const { repository, store, flush, syncState } = useOperator()
  const online = useOnline()
  const [hours, setHours] = useState('')
  const [touched, setTouched] = useState(false)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const noWork = items.every((item) => item.events.length === 0)
  const parsed = parseEngineHours(hours)
  const below = parsed.ok && parsed.value < mission.startEngineHours
  const { completed, waiting, problem } = mission.progress
  const unresolved = waiting + problem
  const remaining = items
    .filter((item) => item.status !== 'COMPLETED')
    .sort((a, b) => a.order - b.order)
  const durations = missionDurations({
    startedAt: mission.startedAt,
    endedAt: null,
    pausedSeconds: mission.pausedSeconds,
    startEngineHours: mission.startEngineHours,
    endEngineHours: null,
  })
  const pendingPoints = syncState?.pending ?? 0
  const engineDelta =
    parsed.ok && !below ? Math.round((parsed.value - mission.startEngineHours) * 100) / 100 : null

  async function end() {
    setTouched(true)
    setError(null)
    if (!parsed.ok || below) return
    setPending(true)
    // Les positions déjà relevées partent d'abord (le serveur accepte aussi celles capturées avant la fin).
    await flush()
    const outcome = await runCommand(store, `end:${mission.id}`, (idempotencyKey) =>
      repository.end({ idempotencyKey, missionId: mission.id, endEngineHours: parsed.normalized }),
    )
    setPending(false)
    if (outcome.kind === 'ok') {
      await onEnded()
      return
    }
    setError(
      outcome.kind === 'refused'
        ? serverMessage(outcome.code)
        : 'Le serveur n’a pas répondu. Vérifiez le réseau et confirmez de nouveau : la même demande sera renvoyée.',
    )
  }

  async function cancelStart() {
    setError(null)
    setPending(true)
    const outcome = await runCommand(store, `cancel-start:${mission.id}`, (idempotencyKey) =>
      repository.cancelStart({ idempotencyKey, missionId: mission.id }),
    )
    setPending(false)
    setConfirmCancel(false)
    if (outcome.kind === 'ok') {
      await onEnded()
      return
    }
    setError(
      outcome.kind === 'refused'
        ? serverMessage(outcome.code)
        : 'Le serveur n’a pas répondu. Vérifiez le réseau et réessayez : la même demande sera renvoyée.',
    )
  }

  return (
    <div className="op-review" role="dialog" aria-modal="true" aria-label="Terminer ma sortie">
      <section className="op-review__main">
        <span className="field-eyebrow">
          Mission n° {mission.number ?? '—'} · {mission.routeName}
        </span>
        <h2>
          {unresolved > 0
            ? `Fin de sortie, ${String(unresolved)} résidence${unresolved > 1 ? 's' : ''} non résolue${unresolved > 1 ? 's' : ''}`
            : 'Fin de sortie, tout est fait'}
        </h2>
        <dl className="op-review__tiles">
          <div className="op-review__tile op-review__tile--done">
            <dt>Terminées</dt>
            <dd>{completed}</dd>
          </div>
          <div className="op-review__tile op-review__tile--todo">
            <dt>Restantes</dt>
            <dd>{waiting}</dd>
          </div>
          <div className="op-review__tile op-review__tile--problem">
            <dt>Problèmes</dt>
            <dd>{problem}</dd>
          </div>
          <div className="op-review__tile">
            <dt>Durée</dt>
            <dd>{formatDuration(durations.elapsedSeconds)}</dd>
          </div>
          <div className="op-review__tile">
            <dt>Pauses</dt>
            <dd>{formatDuration(durations.pausedSeconds)}</dd>
          </div>
        </dl>
        {remaining.length > 0 ? (
          <div className="op-review__remaining">
            <span className="field-eyebrow">Restent au bureau</span>
            <ul>
              {remaining.map((item) => (
                <li key={item.id}>
                  <span className="op-order">{item.order}</span> {itemAddress(item.snapshot)}
                  <span className={`op-chip op-chip--${item.status === 'PROBLEM' ? 'problem' : 'todo'}`}>
                    {item.status === 'PROBLEM' ? 'Problème' : 'À faire'}
                  </span>
                </li>
              ))}
            </ul>
            <p className="op-muted">Elles ne sont ni effacées ni comptées comme faites : le bureau les voit et les traitera.</p>
          </div>
        ) : null}
      </section>

      <aside className="op-review__side">
        <label className="op-field op-field--hours" htmlFor="op-end-hours">
          <span>Compteur d’heures du tracteur au retour</span>
        </label>
        <input
          id="op-end-hours"
          className="op-input op-review__hours"
          inputMode="decimal"
          autoComplete="off"
          value={hours}
          aria-invalid={(touched && !parsed.ok) || below}
          aria-describedby="op-end-help"
          onChange={(event) => {
            setHours(event.target.value)
          }}
        />
        <small id="op-end-help" className="op-muted">
          Départ {formatEngineHours(mission.startEngineHours)}
          {engineDelta !== null ? ` → ${formatEngineHours(engineDelta)} moteur pour cette sortie.` : '.'}
        </small>
        {touched && !parsed.ok ? <p className="field-reason__error">{engineHoursErrors[parsed.reason]}</p> : null}
        {below ? (
          <p className="field-reason__error" role="alert">
            Le relevé de retour ne peut pas être inférieur au relevé de départ.
          </p>
        ) : null}

        <div className={`op-review__sync${pendingPoints === 0 && online ? ' is-done' : ''}`} role="status">
          {pendingPoints === 0 && online ? <CircleCheck aria-hidden="true" /> : <Clock3 aria-hidden="true" />}
          <div>
            <strong>
              {pendingPoints === 0 && online
                ? 'Tout est envoyé'
                : `${String(pendingPoints)} position${pendingPoints > 1 ? 's' : ''} en attente d’envoi`}
            </strong>
            <span>
              {pendingPoints === 0 && online
                ? 'Les positions de la sortie sont arrivées au serveur.'
                : 'Elles partent avant la fin si le réseau le permet; sinon elles restent sur la tablette et partiront plus tard.'}
            </span>
          </div>
        </div>

        {error ? (
          <p className="field-reason__error" role="alert">
            {error}
          </p>
        ) : null}
        <div className="op-review__actions">
          <button type="button" className="field-button field-button--outline field-button--tall" disabled={pending} onClick={onClose}>
            <ArrowLeft aria-hidden="true" /> Retour à la mission
          </button>
          <button type="button" className="op-start-button" disabled={pending} onClick={() => void end()}>
            <Flag aria-hidden="true" /> {pending ? 'Enregistrement…' : 'Confirmer la fin de sortie'}
          </button>
        </div>
        {noWork ? (
          <div className="op-review__cancel">
            {confirmCancel ? (
              <>
                <p>
                  Annuler ce démarrage ? La mission sera classée « démarrage accidentel », hors des heures et des
                  performances; vos résidences restent à faire.
                </p>
                <div className="op-confirm__actions">
                  <button
                    type="button"
                    className="field-button field-button--outline"
                    disabled={pending}
                    onClick={() => {
                      setConfirmCancel(false)
                    }}
                  >
                    Garder la mission
                  </button>
                  <button
                    type="button"
                    className="field-button field-button--danger"
                    disabled={pending}
                    onClick={() => void cancelStart()}
                  >
                    Confirmer l’annulation
                  </button>
                </div>
              </>
            ) : (
              <button
                type="button"
                className="field-button field-button--outline"
                disabled={pending}
                onClick={() => {
                  setConfirmCancel(true)
                }}
              >
                <Undo2 aria-hidden="true" /> Annuler le démarrage
              </button>
            )}
          </div>
        ) : null}
      </aside>
    </div>
  )
}
